// What a page says about itself: banners for what's wrong or worth knowing (an error with Try again), an empty state
// with the one thing to do, and skeletons while it loads.
import type { ReactNode } from 'react';
import { Button } from './Button';
import { cx } from './cx';
import { ArrowClockwiseIcon, CheckCircleIcon, InfoIcon, WarningCircleIcon, WarningIcon } from './icons';
import s from './Message.module.css';

const ICONS = {
  info: <InfoIcon size={20} weight="fill" aria-hidden="true" />,
  good: <CheckCircleIcon size={20} weight="fill" aria-hidden="true" />,
  warn: <WarningIcon size={20} weight="fill" aria-hidden="true" />,
  error: <WarningCircleIcon size={20} weight="fill" aria-hidden="true" />,
};

/** A message on the page. An error is announced at once; the rest politely. */
export function Banner({
  tone = 'info',
  title,
  action,
  children,
}: {
  tone?: 'info' | 'good' | 'warn' | 'error';
  title?: ReactNode;
  action?: ReactNode;
  children?: ReactNode;
}) {
  return (
    <div className={cx(s.banner, s[tone])} role={tone === 'error' ? 'alert' : 'status'}>
      {ICONS[tone]}
      <div className={s.bannerText}>
        {title && <span className={s.bannerTitle}>{title}</span>}
        {children && <span>{children}</span>}
      </div>
      {action && <div className={s.bannerAction}>{action}</div>}
    </div>
  );
}

/** A load that failed: what went wrong, and Try again. */
export function ErrorBanner({ children, retry }: { children: ReactNode; retry?: () => void }) {
  return (
    <Banner
      tone="error"
      action={
        retry && (
          <Button variant="gray" size="small" icon={<ArrowClockwiseIcon size={16} weight="bold" />} onClick={retry}>
            Try again
          </Button>
        )
      }
    >
      {children}
    </Banner>
  );
}

/** Nothing here yet: what would be, and the one thing to do about it. */
export function EmptyState({
  icon,
  title,
  children,
  action,
}: {
  icon?: ReactNode;
  title: string;
  children?: ReactNode;
  action?: ReactNode;
}) {
  return (
    <div className={s.empty}>
      {icon && (
        <div className={s.emptyIcon} aria-hidden="true">
          {icon}
        </div>
      )}
      <h2 className={s.emptyTitle}>{title}</h2>
      {children && <div className={s.emptyText}>{children}</div>}
      {action && <div className={s.emptyAction}>{action}</div>}
    </div>
  );
}

/**
 * The shape of what's loading, shown only after a moment so a quick load doesn't flash. A screen reader hears
 * "Loading".
 */
export function Skeleton({
  lines = 3,
  blocks = 0,
  label = 'Loading',
}: {
  lines?: number;
  blocks?: number;
  label?: string;
}) {
  return (
    <div className={cx(s.skeleton, s.delayed)} role="status" aria-live="polite">
      <span className="visually-hidden">{label}</span>
      {Array.from({ length: blocks }, (_, i) => (
        <div key={`b${i}`} className={cx(s.bone, s.block)} aria-hidden="true" />
      ))}
      {Array.from({ length: lines }, (_, i) => (
        <div key={`l${i}`} className={s.bone} aria-hidden="true" />
      ))}
    </div>
  );
}
